import React, { useMemo, useState } from "react";
import {
  Title,
  Select,
  GoBack,
  TextArea,
  MyMessage,
  PhoneInput,
  InputField,
  DashboardIntro,
  MyActionButton,
  MyButton,
} from "../../home/component/Component";
import { BreadCrumbComponent } from "../../../middlewares/breadCrumb/BreadCrumbComponent";
import { useDoSeeDataContext } from "../../../context/doSeeData/DoSeeDataContext";
import { getOneParty } from "../../../middlewares/sysNavigation/SysNavigation";
import { FOLDERS, image_kit_folder } from "../utils/Utils";
import { defaultImage } from "../../../middlewares/libraries/imageKit/ImageKit";
import { ImageKitUpload } from "../../../middlewares/libraries/imageKit/ImageKitLibrary";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { useGeoPositionContext } from "../utils/geoPosition";
import { _TYPES_ } from "../utils/types";
import Pricing from "../../home/component/Pricing";
import GenerateDescription from "../utils/GenerateDescription";

/* ---------------- helpers ---------------- */
const toSlug = (s) =>
  (s || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_");

const toList = (s) =>
  (s || "")
    .split("|")
    .map((x) => x.trim())
    .filter(Boolean);

const toStringRepeat = (arr) => (arr?.length ? arr.join("|") : null);

const REPEAT_OPTIONS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
  "annual",
];

const Edit = () => {
  const { updateOneContent } = useDoSeeDataContext();
  const party = getOneParty();

  const {
    country_id,
    entry_label,
    country_name,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
  } = useGeoPositionContext();

  /* ---------------- design tokens ---------------- */
  const container = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";
  const card = "rounded-2xl border border-slate-200 bg-white shadow-sm";
  const sectionPad = "p-4 sm:p-5";
  const label = "text-xs font-semibold text-slate-600";
  const chipsBase =
    "inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs cursor-pointer select-none transition-colors";
  const chipOn = "bg-blue-50 text-blue-700 border-blue-200";
  const chipOff = "bg-white text-slate-700 border-slate-200 hover:bg-slate-50";

  /* ---------------- state ---------------- */
  const party_id = party?.party_id;

  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [title, setTitle] = useState(party?.title || "");
  const [thumbnail, setThumbnail] = useState(party?.thumbnail || defaultImage);
  const [thumbnailId, setThumbnailId] = useState(
    party?.image_cdn_id || "img_cdn_id"
  );

  const [venue, setVenue] = useState(party?.venue || "");
  const [venueCapacity, setVenueCapacity] = useState(
    party?.venue_capacity || ""
  );
  const [startDate, setStartDate] = useState(party?.start_date || "");
  const [endDate, setEndDate] = useState(party?.end_date || "");
  const [startTime, setStartTime] = useState(party?.start_time || "");
  const [endTime, setEndTime] = useState(party?.end_time || "");

  const [email, setEmail] = useState(party?.email_address || "");
  const [payLink, setPayLink] = useState(party?.pay_link);
  const [phoneNumber, setPhoneNumber] = useState(party?.phone_number || "");
  const [whatsapp, setWhatsapp] = useState(party?.whatsapp || "none");
  const [facebook, setFacebook] = useState(party?.facebook || "none");
  const [twitter, setTwitter] = useState(party?.twitter || "none");
  const [instagram, setInstagram] = useState(party?.instagram || "none");
  const [youtube, setYoutube] = useState(party?.youtube || "none");
  const [hashtags, setHashtags] = useState(party?.hashtag || "");

  const [googleMapCode, setGoogleMapCode] = useState(
    party?.google_map_code || "none"
  );
  const [latitude, setLatitude] = useState(party?.latitude || "none");
  const [longitude, setLongitude] = useState(party?.longitude || "none");
  const [formattedAddress, setFormattedAddress] = useState(
    party?.formatted_address || ""
  );

  const [featured, setFeatured] = useState(party?.featured ?? "no");
  const [promoted, setPromoted] = useState(party?.promoted ?? "no");
  const [partyStatus, setPartyStatus] = useState(party?.status ?? 1);
  const [type, setType] = useState(party?.type || "public");
  const [expo, setExpo] = useState(party?.expo ?? 0);
  const [showTop, setShowTop] = useState(party?.show_top ?? 0);

  const [repeatEveryList, setRepeatEveryList] = useState(
    toList(party?.repeat_every)
  );
  const [description, setDescription] = useState(party?.description || "");
  const [pricing, setPricing] = useState(party?.Pricings || []);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);

  const repeatEvery = useMemo(
    () => toStringRepeat(repeatEveryList),
    [repeatEveryList]
  );

  const partyRoutes = "/data/party";

  const responses = (isError, msg) => {
    setError(isError);
    setLoading(false);
    setSuccess(!isError);
    setErrorMessage(msg || "");
  };

  /* ---------------- validation ---------------- */
  const validate = () => {
    if (!title?.trim()) return "Title is required.";
    if (!thumbnail || !thumbnailId) return "Image is required.";
    if (startDate && endDate && new Date(startDate) > new Date(endDate))
      return "End date cannot be before start date.";
    return null;
  };

  const updateOnePartyHandler = async (e) => {
    e.preventDefault();
    setLoading(true);
    const err = validate();
    if (err) {
      responses(true, err);
      return;
    }
    await sendDBRequest();
  };

  /* ---------------- submit ---------------- */
  const sendDBRequest = async () => {
    const data = {
      country_id: country_id ? country_id : party?.country_id,
      first_level_id: first_level_id || 0,
      second_level_id: second_level_id || 0,
      third_level_id: third_level_id || 0,
      fourth_level_id: fourth_level_id || 0,

      title: title?.trim(),
      slug: title ? toSlug(title) : null,
      description,

      venue: venue || null,
      venue_capacity: venueCapacity || null,
      start_date: startDate || null,
      end_date: endDate || null,
      start_time: startTime || null,
      end_time: endTime || null,

      thumbnail: thumbnail || defaultImage,
      image_cdn_id: thumbnailId || "",

      phone_number: phoneNumber || null,
      email_address: email || null,
      pay_link: payLink,
      whatsapp: whatsapp || "none",
      facebook: facebook || "none",
      twitter: twitter || "none",
      instagram: instagram || "none",
      youtube: youtube || "none",
      hashtag: hashtags || "",

      audio_adv: "none",
      google_map_code: googleMapCode || "none",
      latitude: latitude || "none",
      longitude: longitude || "none",
      formatted_address: formattedAddress || null,
      country_short: "none",

      status: partyStatus,
      featured,
      promoted,
      type,
      repeat_every: repeatEvery,
      expo,
      show_top: showTop,
      pricing,
    };

    const { message, status } = await updateOneContent({
      data_id: party_id,
      data,
      content: _TYPES_.PARTY,
      position: {
        country_id,
        first_level_id,
        second_level_id,
        third_level_id,
        fourth_level_id,
      },
    });

    if (status === 200) responses(false, message);
    else responses(true, message);
  };

  const toggleRepeat = (val) => {
    setRepeatEveryList((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );
  };

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className={`${container} py-4 lg:py-6`}>
        <div className="mb-3">
          <BreadCrumbComponent />
        </div>
        <div className="flex items-center justify-between mb-2">
          <GoBack props={{ route: partyRoutes }} />
        </div>
        <div className="mb-4">
          <DashboardIntro props={{ intro: `Edit Party`, name: entry_label }} />
        </div>

        {/* Flash */}
        <div className={`${card} ${sectionPad} mb-4`}>
          <MyMessage
            error={error}
            message={message}
            success={success}
            onExpired={() => {
              setError(false);
              setSuccess(false);
            }}
            onClick={() => {
              setError(false);
              setSuccess(false);
              setErrorMessage("");
            }}
          />
        </div>

        <form onSubmit={updateOnePartyHandler}>
          {/* Media */}
          <div className={`${card} ${sectionPad} mb-4`}>
            <p className={label}>Cover Image</p>
            <div className="mt-2">
              <ImageKitUpload
                thumbnail={thumbnail}
                thumbnailId={thumbnailId}
                setThumbnail={setThumbnail}
                setThumbnailId={setThumbnailId}
                folder={image_kit_folder(country_name, FOLDERS.PARTY)}
              />
            </div>
          </div>

          {/* Details */}
          <div className={`${card} ${sectionPad} mb-4`}>
            <Title props={{ title: "Party Details" }} />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-3">
              <InputField
                props={{
                  id: "title",
                  title: "Title",
                  type: "text",
                  value: title,
                  onChange: (e) => setTitle(e.target.value),
                }}
              />

              <Select
                props={{
                  id: "top",
                  title: "Show In Top Parties",
                  option: ["no", "yes"],
                  preselect:
                    showTop !== null
                      ? [{ name: showTop === 0 ? "no" : "yes" }]
                      : null,
                  onChange: (e) =>
                    setShowTop(e.target.selectedIndex === 0 ? 0 : 1),
                }}
              />

              {/* Type segmented toggle */}
              <div className="flex flex-col gap-2">
                <p className={label}>Type</p>
                <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 w-fit">
                  <button
                    type="button"
                    onClick={() => setType("public")}
                    className={`px-3 py-1.5 rounded-md text-sm ${
                      type === "public"
                        ? "bg-blue-600 text-white"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    Public
                  </button>
                  <button
                    type="button"
                    onClick={() => setType("private")}
                    className={`px-3 py-1.5 rounded-md text-sm ${
                      type === "private"
                        ? "bg-blue-600 text-white"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    Private
                  </button>
                </div>
              </div>

              <InputField
                props={{
                  id: "venue",
                  title: "Venue",
                  type: "text",
                  value: venue,
                  onChange: (e) => setVenue(e.target.value),
                }}
              />
              <InputField
                props={{
                  id: "venue_capacity",
                  title: "Venue Capacity",
                  type: "number",
                  value: venueCapacity,
                  onChange: (e) => setVenueCapacity(e.target.value),
                }}
              />
              <InputField
                props={{
                  id: "formatted_address",
                  title: "Formatted Address",
                  type: "address",
                  value: formattedAddress,
                  onChange: (e) => setFormattedAddress(e.target.value),
                }}
              />
            </div>
          </div>

          {/* Date & Time */}
          <div className={`${card} ${sectionPad} mb-4`}>
            <Title props={{ title: "Date & Time" }} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-3">
              <InputField
                props={{
                  id: "start_date",
                  title: "Start Date",
                  type: "date",
                  value: startDate,
                  onChange: (e) => setStartDate(e.target.value),
                }}
              />
              <InputField
                props={{
                  id: "end_date",
                  title: "End Date",
                  type: "date",
                  value: endDate,
                  onChange: (e) => setEndDate(e.target.value),
                }}
              />
              <InputField
                props={{
                  id: "start_time",
                  title: "Start Time",
                  type: "time",
                  value: startTime,
                  onChange: (e) => setStartTime(e.target.value),
                }}
              />
              <InputField
                props={{
                  id: "end_time",
                  title: "End Time",
                  type: "time",
                  value: endTime,
                  onChange: (e) => setEndTime(e.target.value),
                }}
              />
            </div>

            {/* Repeat Every */}
            <div className="mt-5">
              <p className={label}>Repeat Every</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {REPEAT_OPTIONS.map((opt) => {
                  const on = repeatEveryList.includes(opt);
                  return (
                    <span
                      key={opt}
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleRepeat(opt)}
                      onKeyDown={(e) =>
                        (e.key === "Enter" || e.key === " ") &&
                        toggleRepeat(opt)
                      }
                      className={`${chipsBase} ${
                        on ? chipOn : chipOff
                      } capitalize`}
                    >
                      {on ? (
                        <MdCheckBox className="text-base" />
                      ) : (
                        <MdCheckBoxOutlineBlank className="text-base" />
                      )}
                      {opt}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className={`${card} ${sectionPad} mb-4`}>
            <Title props={{ title: "Pricing" }} />
            <Pricing pricing={pricing} setPricing={setPricing} />
          </div>

          {/* Contact */}
          <div className={`${card} ${sectionPad} mb-4`}>
            <Title props={{ title: "Contact Info" }} />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-3">
              <div className="flex items-center">
                <PhoneInput
                  setPhone={setPhoneNumber}
                  phone={phoneNumber}
                  required={false}
                />
              </div>
              <InputField
                props={{
                  id: "email",
                  title: "Email Address",
                  type: "email",
                  value: email,
                  onChange: (e) => setEmail(e.target.value),
                }}
              />
              <InputField
                props={{
                  id: "pay-link",
                  title: "Pay Link (Optional)",
                  type: "text",
                  value: payLink ? payLink : null,
                  onChange: (e) => {
                    setPayLink(e.target.value);
                    setError(null);
                    setSuccess(null);
                  },
                }}
              />
            </div>

            <Title props={{ title: "Social Contact" }} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-3">
              <InputField
                props={{
                  id: "whatsapp",
                  title: "Whatsapp",
                  type: "text",
                  value: whatsapp,
                  onChange: (e) => setWhatsapp(e.target.value),
                }}
              />
              <InputField
                props={{
                  id: "facebook",
                  title: "Facebook",
                  type: "text",
                  value: facebook,
                  onChange: (e) => setFacebook(e.target.value),
                }}
              />
              <InputField
                props={{
                  id: "twitter",
                  title: "Twitter",
                  type: "text",
                  value: twitter,
                  onChange: (e) => setTwitter(e.target.value),
                }}
              />
              <InputField
                props={{
                  id: "instagram",
                  title: "Instagram",
                  type: "text",
                  value: instagram,
                  onChange: (e) => setInstagram(e.target.value),
                }}
              />
              <InputField
                props={{
                  id: "youtube",
                  title: "YouTube",
                  type: "text",
                  value: youtube,
                  onChange: (e) => setYoutube(e.target.value),
                }}
              />
            </div>
          </div>

          {/* Geo */}
          <div className={`${card} ${sectionPad} mb-4`}>
            <Title props={{ title: "Geo Position" }} />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-3">
              <InputField
                props={{
                  id: "google_map_code",
                  title: "Google Map Code",
                  type: "text",
                  value: googleMapCode,
                  onChange: (e) => setGoogleMapCode(e.target.value),
                }}
              />
              <InputField
                props={{
                  id: "latitude",
                  title: "Latitude",
                  type: "text",
                  value: latitude,
                  onChange: (e) => setLatitude(e.target.value),
                }}
              />
              <InputField
                props={{
                  id: "longitude",
                  title: "Longitude",
                  type: "text",
                  value: longitude,
                  onChange: (e) => setLongitude(e.target.value),
                }}
              />
            </div>
          </div>

          {/* Settings */}
          <div className={`${card} ${sectionPad} mb-4`}>
            <Title props={{ title: "More Settings" }} />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-3">
              <Select
                props={{
                  id: "party_status",
                  title: "Status",
                  option: ["visible", "invisible"],
                  preselect:
                    partyStatus !== null
                      ? [{ name: partyStatus === 1 ? "visible" : "invisible" }]
                      : null,
                  onChange: (e) =>
                    setPartyStatus(e.target.selectedIndex === 0 ? 1 : 0),
                }}
              />
              <Select
                props={{
                  id: "featured",
                  title: "Featured",
                  option: ["no", "yes"],
                  preselect: featured !== null ? [{ name: featured }] : null,
                  onChange: (e) => setFeatured(e.target.value),
                }}
              />
              <Select
                props={{
                  id: "promoted",
                  title: "Promoted",
                  option: ["no", "yes"],
                  preselect: promoted !== null ? [{ name: promoted }] : null,
                  onChange: (e) => setPromoted(e.target.value),
                }}
              />
              <Select
                props={{
                  id: "gallery",
                  title: "Show in Gallery",
                  option: ["no", "yes"],
                  preselect:
                    expo !== null
                      ? [{ name: expo === 0 ? "no" : "yes" }]
                      : null,
                  onChange: (e) => setExpo(e.target.value === "no" ? 0 : 1),
                }}
              />
            </div>
          </div>

          {/* Description */}
          <div className={`${card} ${sectionPad} mb-4`}>
            {showDescriptionModal ? (
              <GenerateDescription
                setDescription={setDescription}
                setShowDescriptionModal={setShowDescriptionModal}
              />
            ) : null}

            <div className="flex items-center gap-2 mb-3">
              <MyButton
                onClick={() => setShowDescriptionModal(true)}
                className="bg-blue-700 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
              >
                Generate Description
              </MyButton>
              <span className="text-xs text-slate-500">
                Use AI to draft a description, then tweak below.
              </span>
            </div>

            <TextArea
              props={{
                id: "description",
                title: "Description",
                type: "text",
                value: description,
                onChange: (e) => setDescription(e.target.value),
              }}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <InputField
                props={{
                  id: "hashtag",
                  title: "Tags (comma separated)",
                  type: "text",
                  value: hashtags,
                  onChange: (e) => setHashtags(e.target.value),
                }}
              />
            </div>
          </div>

          {/* Submit */}
          <div className={`${card} ${sectionPad} sticky bottom-0`}>
            <div className="flex justify-end">
              <MyActionButton
                loading={loading}
                title="Save Changes"
                onClick={() => {
                  setError(false);
                  setSuccess(false);
                  setErrorMessage("");
                }}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Edit;
